import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardStats } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, getStatusColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Truck,
  Plus,
  AlertTriangle,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Database,
  Loader2,
  Clock,
  CheckCircle2,
  ShieldCheck,
  ShieldAlert,
  CalendarDays,
  Package,
  AlertCircle,
  TrendingUp,
  Scale,
  Timer,
  Target,
  Boxes,
  BarChart3,
  ShoppingCart,
} from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await getDashboardStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    if (!confirm('This will add demo customers, products, and batches. Continue?')) return;
    
    setSeeding(true);
    try {
      const response = await api.post('/seed-demo-data');
      toast.success(response.data.message);
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isEmpty = !stats?.customers_count && !stats?.products_count && !stats?.batches_count;
  const hasAlerts = (stats?.alerts?.overdue_orders?.length > 0) || 
                    (stats?.stock?.expiring_7d > 0) || 
                    (stats?.qc?.on_hold > 0);

  // Calculate trend indicators
  const weightTrend = stats?.this_week?.weight_change_pct || 0;
  const ordersTrend = stats?.this_week?.orders_change_pct || 0;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard">
      {/* Header with Quick Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-xl font-semibold">Operations Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            {new Date().toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild data-testid="btn-new-dispatch" size="sm">
            <Link to="/dispatch/new">
              <Plus className="w-4 h-4 mr-1" />
              New Dispatch
            </Link>
          </Button>
          <Button asChild size="sm" variant="secondary" data-testid="btn-quick-order">
            <Link to="/quick-order">
              <ShoppingCart className="w-4 h-4 mr-1" />
              Operator View
            </Link>
          </Button>
          <Button variant="outline" asChild size="sm">
            <Link to="/planner">
              <CalendarDays className="w-4 h-4 mr-1" />
              Planner
            </Link>
          </Button>
          <Button variant="outline" asChild size="sm">
            <Link to="/qc">
              <ShieldCheck className="w-4 h-4 mr-1" />
              QC
            </Link>
          </Button>
          {isEmpty && user?.role === 'super_admin' && (
            <Button variant="outline" onClick={handleSeedData} disabled={seeding} size="sm">
              {seeding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
              Demo Data
            </Button>
          )}
        </div>
      </div>

      {/* Alerts Banner */}
      {hasAlerts && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-amber-800 text-sm">Attention Required</p>
                <div className="flex flex-wrap gap-3 mt-1 text-xs text-amber-700">
                  {stats?.today?.overdue > 0 && (
                    <Link to="/dispatches" className="hover:underline flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {stats.today.overdue} overdue order(s)
                    </Link>
                  )}
                  {stats?.qc?.on_hold > 0 && (
                    <Link to="/qc" className="hover:underline flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" />
                      {stats.qc.on_hold} batch(es) awaiting QC
                    </Link>
                  )}
                  {stats?.stock?.expiring_7d > 0 && (
                    <Link to="/batches" className="hover:underline flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {stats.stock.expiring_7d} batch(es) expiring in 7 days
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Operational Metrics - Today */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Due Today</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats?.today?.due || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">orders to dispatch</p>
              </div>
              <Timer className="w-8 h-8 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Completed Today</p>
                <p className="text-2xl sm:text-3xl font-bold text-green-600">{stats?.today?.completed || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">dispatched</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${stats?.today?.overdue > 0 ? 'border-l-red-500' : 'border-l-gray-300'}`}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Overdue</p>
                <p className={`text-2xl sm:text-3xl font-bold ${stats?.today?.overdue > 0 ? 'text-red-600' : ''}`}>
                  {stats?.today?.overdue || 0}
                </p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">need attention</p>
              </div>
              <Clock className={`w-8 h-8 ${stats?.today?.overdue > 0 ? 'text-red-500' : 'text-gray-400'} opacity-80`} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Ready to Ship</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats?.dispatches?.locked || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">locked orders</p>
              </div>
              <Package className="w-8 h-8 text-amber-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Throughput & Fulfillment */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Week's Throughput - Weight */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">This Week</p>
              <Scale className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{stats?.this_week?.total_weight_kg?.toLocaleString() || 0} kg</p>
            <div className="flex items-center gap-1 mt-1">
              {!stats?.this_week?.has_last_week_data ? (
                <span className="text-xs text-muted-foreground">No orders last week</span>
              ) : weightTrend !== 0 ? (
                <>
                  {weightTrend > 0 ? (
                    <ArrowUp className="w-3 h-3 text-green-500" />
                  ) : (
                    <ArrowDown className="w-3 h-3 text-red-500" />
                  )}
                  <span className={`text-xs ${weightTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {Math.abs(weightTrend)}% vs last week
                  </span>
                </>
              ) : (
                <span className="text-xs text-muted-foreground">Same as last week</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Week's Orders */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Week's Orders</p>
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{stats?.this_week?.total_orders || 0}</p>
            <div className="flex items-center gap-1 mt-1">
              {stats?.this_week?.total_dispatched > 0 && (
                <span className="text-xs text-green-600">{stats.this_week.total_dispatched} dispatched</span>
              )}
              {stats?.this_week?.total_dispatched === 0 && stats?.this_week?.total_orders > 0 && (
                <span className="text-xs text-amber-600">0 dispatched yet</span>
              )}
              {!stats?.this_week?.has_last_week_data ? (
                <span className="text-xs text-muted-foreground">No orders last week</span>
              ) : ordersTrend !== 0 ? (
                <>
                  {ordersTrend > 0 ? (
                    <ArrowUp className="w-3 h-3 text-green-500" />
                  ) : (
                    <ArrowDown className="w-3 h-3 text-red-500" />
                  )}
                  <span className={`text-xs ${ordersTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {Math.abs(ordersTrend)}% vs last week
                  </span>
                </>
              ) : (
                <span className="text-xs text-muted-foreground">Same as last week</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Cases This Week */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Cases This Week</p>
              <Boxes className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{stats?.this_week?.total_cases?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">this week</p>
          </CardContent>
        </Card>

        {/* Fulfillment Rate */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Fulfillment Rate</p>
              <Target className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className={`text-2xl font-bold ${(stats?.fulfillment?.on_time_rate || 0) >= 95 ? 'text-green-600' : (stats?.fulfillment?.on_time_rate || 0) >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
              {stats?.fulfillment?.on_time_rate ?? 0}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.fulfillment?.total_dispatched_30d || 0} of {stats?.fulfillment?.total_orders_30d || 0} dispatched (30d)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* QC Status, Stock Health & Order Pipeline */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {/* QC Status */}
        <Card>
          <CardHeader className="py-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              QC Status
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {stats?.qc?.pass_rate || 100}% pass rate
              </span>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/qc" className="text-xs">
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Link to="/qc" className="p-2 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors">
                <p className="text-xl font-bold text-amber-600">{stats?.qc?.on_hold || 0}</p>
                <p className="text-[10px] text-amber-700">On Hold</p>
              </Link>
              <div className="p-2 rounded-lg bg-green-50">
                <p className="text-xl font-bold text-green-600">{stats?.qc?.released || 0}</p>
                <p className="text-[10px] text-green-700">Released</p>
              </div>
              <div className="p-2 rounded-lg bg-red-50">
                <p className="text-xl font-bold text-red-600">{stats?.qc?.rejected || 0}</p>
                <p className="text-[10px] text-red-700">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stock Health */}
        <Card>
          <CardHeader className="py-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Boxes className="w-4 h-4" />
              Stock Health
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/batches" className="text-xs">
                <ArrowRight className="w-3 h-3" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between py-1">
                <span className="text-sm text-muted-foreground">Available Stock</span>
                <span className="font-mono font-bold">{stats?.stock?.total_available_units?.toLocaleString() || 0} units</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-sm text-muted-foreground">Active Batches</span>
                <span className="font-mono font-bold">{stats?.stock?.active_batches || 0}</span>
              </div>
              <div className="border-t pt-2 mt-2">
                {(stats?.stock?.expiring_7d > 0 || stats?.stock?.expiring_30d > 0) ? (
                  <div className="space-y-1">
                    {stats?.stock?.expiring_7d > 0 && (
                      <div className="flex items-center gap-2 text-red-600 text-xs">
                        <AlertTriangle className="w-3 h-3" />
                        {stats.stock.expiring_7d} expiring in 7 days
                      </div>
                    )}
                    {stats?.stock?.expiring_30d > 0 && (
                      <div className="flex items-center gap-2 text-amber-600 text-xs">
                        <AlertTriangle className="w-3 h-3" />
                        {stats.stock.expiring_30d} expiring in 30 days
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-green-600 text-xs">
                    <CheckCircle2 className="w-3 h-3" />
                    No expiring stock
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Order Pipeline */}
        <Card className="sm:col-span-2 lg:col-span-1">
          <CardHeader className="py-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Order Pipeline
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dispatches" className="text-xs">
                <ArrowRight className="w-3 h-3" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-zinc-400"></div>
                  <span className="text-sm">Draft</span>
                </div>
                <span className="font-mono font-bold">{stats?.dispatches?.draft || 0}</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-sm">In Progress</span>
                </div>
                <span className="font-mono font-bold">{stats?.dispatches?.in_progress || 0}</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                  <span className="text-sm">Locked</span>
                </div>
                <span className="font-mono font-bold">{stats?.dispatches?.locked || 0}</span>
              </div>
              <div className="flex items-center justify-between py-1 border-t pt-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-sm font-medium">Dispatched</span>
                </div>
                <span className="font-mono font-bold text-green-600">{stats?.dispatches?.dispatched || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Operations Workflow - Mental Model (KEEP AS IS) */}
      <Card className="border-2 border-dashed border-primary/20">
        <CardHeader className="py-4">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <ArrowRight className="w-5 h-5 text-primary" />
            Daily Operations Workflow
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Follow this sequence for processing customer orders. Click any step to navigate.
          </p>
        </CardHeader>
        <CardContent className="pb-6">
          <div className="flex flex-col lg:flex-row items-stretch gap-4 lg:gap-2">
            {/* Step 1: Sales Order Entry */}
            <Link to="/sales-order/new" className="flex-1 group">
              <div className="h-full border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 rounded-xl p-4 transition-all cursor-pointer">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">1</div>
                  <h3 className="font-semibold text-blue-900">Sales Order Entry</h3>
                </div>
                <p className="text-xs text-blue-700 mb-3">Create order from customer PO</p>
                <ul className="space-y-1 text-xs text-blue-600">
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-blue-500" />
                    Enter customer PO number
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-blue-500" />
                    Select customer & delivery address
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-blue-500" />
                    Add products & ordered quantities
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-blue-500" />
                    Set dispatch date & transport
                  </li>
                </ul>
              </div>
            </Link>

            {/* Arrow */}
            <div className="hidden lg:flex items-center justify-center px-2">
              <ArrowRight className="w-6 h-6 text-muted-foreground" />
            </div>
            <div className="lg:hidden flex justify-center py-1">
              <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
            </div>

            {/* Step 2: Order Pipeline */}
            <Link to="/planner" className="flex-1 group">
              <div className="h-full border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 rounded-xl p-4 transition-all cursor-pointer">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-sm">2</div>
                  <h3 className="font-semibold text-amber-900">Order Pipeline</h3>
                </div>
                <p className="text-xs text-amber-700 mb-3">Start order & create batches</p>
                <ul className="space-y-1 text-xs text-amber-600">
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-amber-500" />
                    View orders by week/date
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-amber-500" />
                    Click "Start" to begin processing
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-amber-500" />
                    Click "Ready" → create batch numbers
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-amber-500" />
                    Allocate weight, units, cases
                  </li>
                </ul>
              </div>
            </Link>

            {/* Arrow */}
            <div className="hidden lg:flex items-center justify-center px-2">
              <ArrowRight className="w-6 h-6 text-muted-foreground" />
            </div>
            <div className="lg:hidden flex justify-center py-1">
              <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
            </div>

            {/* Step 3: Dispatch Note */}
            <Link to="/dispatches" className="flex-1 group">
              <div className="h-full border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 rounded-xl p-4 transition-all cursor-pointer">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-sm">3</div>
                  <h3 className="font-semibold text-emerald-900">Dispatch Note</h3>
                </div>
                <p className="text-xs text-emerald-700 mb-3">Lock, print & mark dispatched</p>
                <ul className="space-y-1 text-xs text-emerald-600">
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-emerald-500" />
                    Compare ordered vs actual
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-emerald-500" />
                    Lock dispatch note
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-emerald-500" />
                    Print dispatch note & pallet label
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-emerald-500" />
                    Mark as dispatched
                  </li>
                </ul>
              </div>
            </Link>

            {/* Arrow */}
            <div className="hidden lg:flex items-center justify-center px-2">
              <ArrowRight className="w-6 h-6 text-muted-foreground" />
            </div>
            <div className="lg:hidden flex justify-center py-1">
              <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
            </div>

            {/* Step 4: Dispatch View */}
            <Link to="/dispatch-view" className="flex-1 group">
              <div className="h-full border-2 border-teal-200 bg-teal-50 hover:bg-teal-100 hover:border-teal-400 rounded-xl p-4 transition-all cursor-pointer">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-teal-500 text-white flex items-center justify-center font-bold text-sm">4</div>
                  <h3 className="font-semibold text-teal-900">Dispatch View</h3>
                </div>
                <p className="text-xs text-teal-700 mb-3">View all dispatches & notify</p>
                <ul className="space-y-1 text-xs text-teal-600">
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-teal-500" />
                    View all dispatch notes
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-teal-500" />
                    Search by customer, PO, product
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-teal-500" />
                    Reprint pallet labels & notes
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-teal-500" />
                    Email dispatch to customer
                  </li>
                </ul>
              </div>
            </Link>

            {/* Arrow */}
            <div className="hidden lg:flex items-center justify-center px-2">
              <ArrowRight className="w-6 h-6 text-muted-foreground" />
            </div>
            <div className="lg:hidden flex justify-center py-1">
              <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
            </div>

            {/* Step 5: Reports & Trace */}
            <Link to="/reports" className="flex-1 group">
              <div className="h-full border-2 border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-400 rounded-xl p-4 transition-all cursor-pointer">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold text-sm">5</div>
                  <h3 className="font-semibold text-purple-900">Reports & Trace</h3>
                </div>
                <p className="text-xs text-purple-700 mb-3">Traceability & audit reports</p>
                <ul className="space-y-1 text-xs text-purple-600">
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-purple-500" />
                    Trace by batch code
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-purple-500" />
                    Find by customer or product
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-purple-500" />
                    QC status reports
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-purple-500" />
                    Export for audits
                  </li>
                </ul>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Recent Dispatches */}
      <Card>
        <CardHeader className="py-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium">Recent Dispatches</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/dispatches" className="text-xs">
              View all <ArrowRight className="w-3 h-3 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {stats?.recent_dispatches?.length > 0 ? (
            <div className="divide-y">
              {stats.recent_dispatches.map((dispatch) => (
                <Link
                  key={dispatch.id}
                  to={`/dispatch/${dispatch.id}`}
                  className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-sm font-medium">{dispatch.dispatch_number}</p>
                      {dispatch.total_net_weight && (
                        <span className="text-xs text-muted-foreground">
                          {dispatch.total_net_weight} kg
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{dispatch.customer_name}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <Badge className={`${getStatusColor(dispatch.status)} text-[10px]`}>
                      {dispatch.status}
                    </Badge>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {formatDate(dispatch.dispatch_date)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-muted-foreground">
              <Truck className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No dispatches yet</p>
              <Button asChild className="mt-3" size="sm">
                <Link to="/dispatch/new">Create dispatch</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
